import { registerPostRender } from '../core/render.js';
import { catalogoCompat, parametrosCompat } from './fondos/catalogo.js';
import { reconciliar, desmontar, esFondoConocido as _esFondoConocido } from './fondos/motor.js';

/**
 * FONDOS ANIMADOS — la FACHADA de siempre.
 *
 * Desde el P3 del plan del motor de fondos (`CLAUDE_RESUMEN_PLANES/2026-09-24_plan-p3-motor-efectos.md`)
 * el ciclo de vida de los fondos es de UN dueño, `fondos/motor.js`, y lo que antes vivía aquí se
 * repartió por responsabilidades en `animations/fondos/`:
 *   · qué fondos hay y qué entiende cada uno → `catalogo.js` (lo lee también Python);
 *   · la config efectiva (acento, duotono, tono, tope sobre foto, overlay normalizado) → `efectiva.js`;
 *   · las 5 texturas, como fondo y como overlay, byte a byte las de siempre → `texturas.js`;
 *   · la capa del fondo (opacidad y desenfoque a la capa que PINTA, el overlay) → `capa.js`;
 *   · cómo se pinta cada fondo animado → `efectos/index.js`: los 12 lienzos (con humo, mesh y blobs
 *     desde la F3) son efectos propios sobre la base `lienzo.js`, pintados por el bucle único del
 *     motor; aurora, manchas CSS sobre la base `capas.js`, animadas en el compositor; el segundo
 *     color, de `paleta.js`.
 *
 * Aquí quedan los nombres que el resto del editor ya usaba, delegando en el motor, y el hook
 * post-render (en el MISMO punto del orden de carga que siempre: el orden de los hooks importa).
 * Fuera quedaron `BackgroundRegistry`/`OverlayRegistry` (factorías que creaban instancias FUERA de
 * cualquier control: dos dueños es justo lo que el motor viene a quitar) y
 * `registrarInstanciaDeFondo` (la vista previa del BG Studio ya es un fondo del motor).
 */

// El CATÁLOGO vive en `fondos/catalogo.js`, la fuente única que el build exporta también a Python.
// Aquí se conserva la forma de siempre (`{id, label, category: 'canvas'|'css', icon}`).
export const BG_CATALOG = catalogoCompat();

// Qué parámetros entiende cada fondo (y en qué orden los enseña el panel): del catálogo, con sus
// rangos y valores por defecto (`fondos/catalogo.js::rangoDe`). Forma de siempre: `{id: [claves]}`.
export const BG_PARAMS = parametrosCompat();

export { configEfectiva } from './fondos/efectiva.js';

/** ¿Es un fondo que se sabe pintar? (`hasOwnProperty` en los registros del motor.) */
export function esFondoConocido(id) {
    return _esFondoConocido(id);
}

/**
 * Pone los fondos como dicen los atributos de cada sección (`fondos/motor.js::reconciliar`): monta
 * los nuevos, deja como están los que no han cambiado y desmonta los de las secciones que ya no
 * están en el documento.
 * @param {HTMLElement} [raiz] - ÁMBITO opcional. Sin él, toda la página (el hook post-render). Con
 *   él, solo las secciones que cuelgan de ahí: lo usan la vista previa de variantes y el BG Studio al
 *   Aplicar, para no tocar los fondos del resto de la página.
 */
export function initBackgroundAnimations(raiz) {
    reconciliar(raiz);
}

/** Desmonta los fondos de `raiz` (sin ella, todos) y los de secciones que ya no están. */
export function destroyBackgroundAnimations(raiz) {
    desmontar(raiz);
}

/**
 * Para quien cambia el alto de una sección de golpe después del render (el sticky de la timeline
 * v4). Con el motor no hace falta rehacer nada —su ResizeObserver re-mide el lienzo sin reiniciarlo
 * (E6)—, así que reconciliar la sección no cambia nada si su fondo es el mismo.
 * @param {HTMLElement} section
 */
export function rehacerFondoDeSeccion(section) {
    if (!section || typeof section.getAttribute !== 'function') return;
    const tipo = section.getAttribute('data-bg-anim');
    if (!tipo || tipo === 'none') return;
    reconciliar(section);
}

// Hook al final de cada render
registerPostRender(initBackgroundAnimations);
